import { useState } from "react";
import { isArchivedStatus, Registration, Webinar, WebinarStatus, WEBINAR_STATUS_LABELS } from "../../webinars/types";
import { DeliveriesByRegistration, joiningSummary } from "../../webinars/emailStatus";
import { formatShortDate, formatTime } from "../../webinars/format";
import { isPastWebinar } from "../../webinars/availability";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import ActionsMenu, { MenuItem } from "./ActionsMenu";

interface WebinarTableProps {
  webinars: Webinar[];
  /** This round's active registrations, per webinar */
  counts: Record<string, number>;
  /** This round's registrations (archived ones left out) */
  registrations: Registration[];
  deliveries: DeliveriesByRegistration;
  meetingLinks: Record<string, string>;
  onShowAttendees: (webinarId: string) => void;
  onJoiningInstructions: (webinarId: string) => void;
  onAdd: () => void;
  onEdit: (webinarId: string) => void;
}

type RowProps = Omit<WebinarTableProps, "webinars" | "onAdd"> & { webinars: Webinar[]; asCards: boolean };

/** Below this width webinars are shown as cards rather than a table */
const PHONE = "(max-width: 760px)";

// Newest first, so the sessions being worked on are at the top
const newestFirst = (a: Webinar, b: Webinar) => Date.parse(b.startAt) - Date.parse(a.startAt);

const matches = (w: Webinar, query: string) =>
  !query || w.title.toLowerCase().includes(query) || formatShortDate(w.startAt).toLowerCase().includes(query);

/** Everything one webinar's row or card shows */
function describe(w: Webinar, { counts, registrations, deliveries, meetingLinks }: Pick<RowProps, "counts" | "registrations" | "deliveries" | "meetingLinks">) {
  const active = counts[w.id] ?? 0;
  const joining = joiningSummary(registrations.filter(r => r.webinarId === w.id), deliveries);
  const canSendJoining = w.status !== "draft" && w.status !== "cancelled";
  return {
    active,
    percent: w.capacity ? Math.min(100, Math.round((active / w.capacity) * 100)) : 0,
    joining,
    hasLink: Boolean(meetingLinks[w.id]),
    canSendJoining,
    // Shown as a button rather than in the ⋮ menu when there's something to do
    joiningNeedsAction: canSendJoining && !isPastWebinar(w) && joining.registered > 0 && (joining.none > 0 || joining.failed > 0),
  };
}

function Registrations({ w, active, percent }: { w: Webinar; active: number; percent: number }) {
  return w.capacity ? (
    <>
      <strong>{active} / {w.capacity}</strong>
      <div className="wa-bar" role="meter" aria-label="Seats taken" aria-valuemin={0} aria-valuemax={w.capacity} aria-valuenow={active}>
        <div style={{ width: `${percent}%` }} />
      </div>
    </>
  ) : (
    <span><strong>{active}</strong> registered</span>
  );
}

/** "2 / 3 sent · 1 failed", "All sent", "Link missing" … */
function JoiningProgress({ w, active, joining, hasLink }: { w: Webinar; active: number; joining: ReturnType<typeof joiningSummary>; hasLink: boolean }) {
  if (w.status === "draft") return <span className="wa-cell-meta">Not published yet</span>;
  if (w.status === "cancelled") return <span className="wa-cell-meta">Not sent: webinar cancelled</span>;
  // Once it's over, joining instructions no longer matter
  if (isPastWebinar(w)) return <span className="wa-cell-meta">Webinar has finished</span>;
  return (
    <span className="wa-joining">
      {!hasLink && <span className="wa-joining-warn">⚠ Link missing</span>}
      {joining.registered === 0 ? (
        <span className="wa-cell-meta">{active === 0 ? "No attendees yet" : "Nobody waiting for them"}</span>
      ) : joining.done === joining.registered ? (
        <span className="wa-joining-done">✓ All sent</span>
      ) : (
        <span>
          {joining.done} / {joining.registered} sent
          {joining.waiting > 0 && <span className="wa-cell-meta-inline"> · {joining.waiting} waiting</span>}
          {joining.failed > 0 && <span className="wa-joining-warn"> · {joining.failed} failed</span>}
        </span>
      )}
    </span>
  );
}

function Actions({ w, needsJoining, canSendJoining, onShowAttendees, onJoiningInstructions, onEdit }: {
  w: Webinar; needsJoining: boolean; canSendJoining: boolean;
} & Pick<RowProps, "onShowAttendees" | "onJoiningInstructions" | "onEdit">) {
  const menu: MenuItem[] = [{ label: "Edit", onSelect: () => onEdit(w.id) }];
  if (canSendJoining && !needsJoining) menu.push({ label: "Joining instructions", onSelect: () => onJoiningInstructions(w.id) });
  if (w.status !== "draft") menu.push({ label: "View page", to: `/events/${w.slug}` });
  return (
    <div className="wa-row-actions wa-webinar-actions">
      <button type="button" className="wa-btn" onClick={() => onShowAttendees(w.id)}>
        Attendees<span className="km-visuallyHidden">: {w.title}</span>
      </button>
      {needsJoining && (
        <button type="button" className="wa-btn wa-btn--attention" onClick={() => onJoiningInstructions(w.id)}>
          Joining instructions<span className="km-visuallyHidden">: {w.title}</span>
        </button>
      )}
      <ActionsMenu label={`More actions for ${w.title}`} items={menu} />
    </div>
  );
}

function WebinarRows({ webinars, asCards, ...rest }: RowProps) {
  const title = (w: Webinar) => (
    <>
      <strong className="wa-cell-title">
        {w.title}
        {w.isDemo && <span className="wa-chip wa-chip--demo">Demo</span>}
      </strong>
      <small className="wa-cell-meta">{formatShortDate(w.startAt)} · {formatTime(w.startAt)} SAST</small>
    </>
  );
  const status = (w: Webinar) => <span className={`wa-chip wa-chip--${w.status}`}>{WEBINAR_STATUS_LABELS[w.status]}</span>;

  if (asCards) {
    return (
      <ul className="wa-webinar-cards">
        {webinars.map(w => {
          const d = describe(w, rest);
          return (
            <li key={w.id} className="wa-webinar-card">
              <div className="wa-webinar-card-head">
                <div>{title(w)}</div>
                {status(w)}
              </div>
              <dl className="wa-webinar-card-facts">
                <div><dt>Registrations</dt><dd><Registrations w={w} active={d.active} percent={d.percent} /></dd></div>
                <div><dt>Joining instructions</dt><dd><JoiningProgress w={w} active={d.active} joining={d.joining} hasLink={d.hasLink} /></dd></div>
              </dl>
              <Actions w={w} needsJoining={d.joiningNeedsAction} canSendJoining={d.canSendJoining} {...rest} />
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <div className="wa-table-scroll">
      <table className="wa-table wa-table--webinars">
        <thead>
          <tr>
            <th scope="col">Webinar</th>
            <th scope="col">Status</th>
            <th scope="col">Registrations</th>
            <th scope="col">Joining</th>
            <th scope="col" className="wa-actions-col"><span className="km-visuallyHidden">Actions</span></th>
          </tr>
        </thead>
        <tbody>
          {webinars.map(w => {
            const d = describe(w, rest);
            return (
              <tr key={w.id}>
                <td className="wa-webinar-title-cell">{title(w)}</td>
                <td>{status(w)}</td>
                <td className="wa-count-cell"><Registrations w={w} active={d.active} percent={d.percent} /></td>
                <td className="wa-joining-cell"><JoiningProgress w={w} active={d.active} joining={d.joining} hasLink={d.hasLink} /></td>
                <td><Actions w={w} needsJoining={d.joiningNeedsAction} canSendJoining={d.canSendJoining} {...rest} /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

type Tab = "all" | Extract<WebinarStatus, "published" | "draft">;

// Drafts and published webinars are listed first, with search and status tabs. Registration
// closed, completed and cancelled ones move to the Archive, folded away and searchable.
// Desktop: a compact table. Phones: one card per webinar, with the same actions.
export default function WebinarTable({ webinars, onAdd, ...rowProps }: WebinarTableProps) {
  const asCards = useMediaQuery(PHONE);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState<Tab>("all");
  const [archiveSearch, setArchiveSearch] = useState("");

  const active = webinars.filter(w => !isArchivedStatus(w.status)).sort(newestFirst);
  const archived = webinars.filter(w => isArchivedStatus(w.status)).sort(newestFirst);
  const query = search.trim().toLowerCase();
  const shown = active.filter(w => (tab === "all" || w.status === tab) && matches(w, query));
  const archiveQuery = archiveSearch.trim().toLowerCase();
  const archiveMatches = archived.filter(w => matches(w, archiveQuery));
  const tabs: { key: Tab; label: string; count: number }[] = [
    { key: "all", label: "All", count: active.length },
    { key: "published", label: "Published", count: active.filter(w => w.status === "published").length },
    { key: "draft", label: "Draft", count: active.filter(w => w.status === "draft").length },
  ];

  return (
    <section className="wa-panel" id="webinars" aria-labelledby="wa-webinars-title">
      <div className="wa-panel-head">
        <div>
          <h2 id="wa-webinars-title">Webinars</h2>
          <p className="wa-muted wa-panel-sub">Manage events, registrations and joining instructions</p>
        </div>
        <button type="button" className="km-btn km-btn--primary" onClick={onAdd}>+ Add webinar</button>
      </div>

      {active.length > 0 && (
        <div className="wa-webinar-filters">
          <label className="wa-filter wa-filter--search">
            <span className="km-visuallyHidden">Search webinars</span>
            <input type="search" placeholder="Search webinars by title or date" value={search} onChange={e => setSearch(e.target.value)} />
          </label>
          <div className="wa-status-tabs" role="group" aria-label="Show webinars">
            {tabs.map(t => (
              <button key={t.key} type="button" aria-pressed={tab === t.key} onClick={() => setTab(t.key)}>
                {t.label} <span className="wa-tab-count">{t.count}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {active.length === 0 ? (
        <p className="wa-empty">
          {archived.length > 0
            ? "No draft or published webinars. Use Add webinar, or reopen one from the archive below."
            : "No webinars yet. Use Add webinar to create the first one; it starts as a hidden draft."}
        </p>
      ) : shown.length === 0 ? (
        <p className="wa-empty">No {tab === "all" ? "" : `${tab} `}webinars{query ? ` match “${search.trim()}”` : ""}.</p>
      ) : (
        <WebinarRows webinars={shown} asCards={asCards} {...rowProps} />
      )}

      {archived.length > 0 && (
        <details className="wa-archive">
          <summary>
            Archive <span className="wa-archive-count">{archived.length}</span>
            <small>Registration closed, completed and cancelled webinars</small>
          </summary>
          <p className="wa-muted">
            To run one again, <strong>Edit</strong> it and set the status to Draft or Published. That starts a new round:
            its current attendees are archived and the seat count restarts at 0.
          </p>
          <label className="wa-filter wa-filter--search wa-archive-search">
            <span className="km-visuallyHidden">Search the archive</span>
            <input
              type="search"
              placeholder="Search archived webinars by title or date"
              value={archiveSearch}
              onChange={e => setArchiveSearch(e.target.value)}
            />
          </label>
          {archiveMatches.length > 0
            ? <WebinarRows webinars={archiveMatches} asCards={asCards} {...rowProps} />
            : <p className="wa-empty">No archived webinars match “{archiveSearch.trim()}”.</p>}
        </details>
      )}
    </section>
  );
}
